import React from "react";
import { createPortal } from "react-dom";
import { FaMicrophone } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { getDictationLanguageLabel } from "../../../constants/languages";

const POPOVER_SHELL_BASE_CLASS =
  "fixed z-[99999] right-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 flex items-start gap-3 w-[340px] max-w-[calc(100vw-1rem)] sm:w-[360px]";

const POPOVER_POSITION_CLASS = {
  default: "top-1/2 -translate-y-1/2",
  radical: "top-1/3 -translate-y-1/2",
};

/**
 * Agent Mode (compact): a small bar pinned bottom-centre of the agent's report
 * view, below the report text and the floating AI Edit button.
 */
const COMPACT_SHELL_CLASS =
  "fixed z-[99999] left-1/2 -translate-x-1/2 bottom-3 w-[300px] max-w-[calc(100vw-9rem)] min-w-[200px] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 px-2.5 py-2";

const COMPACT_STOP_BUTTON_CLASS =
  "bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white px-2 py-1 rounded-full text-[11px] font-semibold transition-colors cursor-pointer leading-none whitespace-nowrap flex-shrink-0";

const STOP_BUTTON_CLASS =
  "bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white px-3 py-1.5 rounded-full text-sm font-semibold transition-colors cursor-pointer leading-none whitespace-nowrap";

function getModePillClass(isCommandMode, isAiEdit = false) {
  if (isAiEdit) {
    return "inline-flex items-center px-2.5 py-1.5 rounded-full text-[11px] font-semibold border flex-shrink-0 leading-none whitespace-nowrap bg-[#CBEA7B] text-gray-900 border-[#A4C639]";
  }
  return `inline-flex items-center px-2.5 py-1.5 rounded-full text-[11px] font-semibold border flex-shrink-0 leading-none whitespace-nowrap ${
    isCommandMode
      ? "bg-yellow-100 text-yellow-900 dark:bg-yellow-200 dark:text-yellow-900 border-yellow-200 dark:border-yellow-200"
      : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100 border-gray-200 dark:border-gray-600"
  }`;
}

function ModeHotkeysTooltip({ id, title, children, alignStart = false }) {
  return (
    <div
      id={id}
      role="tooltip"
      className={`pointer-events-none absolute z-[60] -top-2 ${
        alignStart ? "left-0" : "right-0"
      } -translate-y-full opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150`}
      aria-hidden="true"
    >
      <div className="relative px-3 py-2 bg-gray-900 text-white rounded-lg shadow-lg text-[11px] whitespace-nowrap">
        <div className="font-semibold mb-1">{title}</div>
        <div className="leading-snug">{children}</div>
        {!alignStart && <div className="absolute right-3 -bottom-1 w-3 h-3 bg-gray-900 transform rotate-45" />}
      </div>
    </div>
  );
}

/** alignStart: compact bar — the tooltip opens from the bar's left edge so it stays on screen. */
function EditorHotkeysTooltip({ id, aiEdit = false, alignStart = false }) {
  return (
    <ModeHotkeysTooltip id={id} title="Hotkeys" alignStart={alignStart}>
      <div>
        <span className="font-mono">F3</span> = Dictation (text + commands)
      </div>
      <div className="mt-0.5">
        <span className="font-mono">Shift+F3</span> = Command mode (commands only)
      </div>
      <div className="mt-0.5">
        <span className="font-mono">F2</span> = Unified mic (routes by last focus)
      </div>
      {aiEdit && (
        <div className="mt-0.5">
          <span className="font-mono">{aiEdit}</span> / “ערוך דוח” / “Edit report” = AI Edit
        </div>
      )}
    </ModeHotkeysTooltip>
  );
}

function SidebarHotkeysTooltip({ id }) {
  return (
    <ModeHotkeysTooltip id={id} title="Sidebar mic">
      <div>
        <span className="font-mono">F2</span> = Toggle mic (routes by last focus)
      </div>
      <div className="mt-0.5">Terminal = Macro command mode (commands only)</div>
    </ModeHotkeysTooltip>
  );
}

/**
 * Shared recording popover shell for editor and sidebar mics.
 */
const RecordingMicPopover = ({
  isActive,
  streamingTranscript = "",
  dictationLanguage = "en-US",
  isCommandMode = false,
  aiEditStatus = null,
  onStop,
  isRadicalAuth = false,
  variant = "editor",
  compact = false,
}) => {
  const { t } = useTranslation("common");
  if (!isActive) return null;

  const languageLabel = getDictationLanguageLabel(dictationLanguage);
  const transcriptText = (streamingTranscript || "").trim();
  const modeLabel = aiEditStatus?.mode && variant === "editor" ? "AI EDIT" : isCommandMode ? "COMMAND" : "DICTATION";
  const aiEditEnabled = !!aiEditStatus && variant === "editor";
  const isAiEdit = aiEditEnabled && !!aiEditStatus.mode;
  const helpText = isAiEdit
    ? t("editReportAi.modeHelp", {
        defaultValue: "Say what to change in the report. Say “undo” to revert.",
      })
    : isCommandMode
      ? "Speak a command (no free text insertion)"
      : aiEditEnabled
        ? t("editReportAi.dictationHelp", {
            defaultValue: "Speak text or a command. Say “Edit report” to edit with AI.",
          })
        : "Speak text or a command";
  const aiEditBusy = aiEditEnabled && aiEditStatus.phase !== "idle";
  const tooltipId =
    variant === "sidebar"
      ? "sidebar-mic-mode-hotkeys-tooltip"
      : "mic-mode-hotkeys-tooltip";

  if (compact) {
    const pillClass = getModePillClass(isCommandMode, isAiEdit).replace("px-2.5 py-1.5 rounded-full text-[11px]", "px-1.5 py-0.5 rounded-full text-[10px]");
    return createPortal(
      <div
        role="status"
        aria-live="polite"
        aria-label={`Voice input active in ${languageLabel}`}
        className={COMPACT_SHELL_CLASS}
        data-testid="mic-popover-compact"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex w-2 h-2 rounded-full bg-green dark:bg-green-dark animate-pulse flex-shrink-0" aria-hidden="true" />
          <p className="min-w-0 flex-1 truncate text-xs leading-none">
            <span className="font-semibold text-gray-700 dark:text-gray-200">Listening...</span>
            <span className="text-gray-500 dark:text-gray-400"> · {languageLabel}</span>
          </p>
          {/* not "relative": the tooltip is placed against the bar (fixed), not the pill */}
          <div className="group inline-flex flex-shrink-0">
            <span
              className={pillClass}
              aria-label={isCommandMode ? "Command mode" : "Dictation mode"}
              aria-describedby={tooltipId}
              tabIndex={0}
            >
              {modeLabel}
            </span>
            <EditorHotkeysTooltip
              id={tooltipId}
              alignStart
              aiEdit={aiEditEnabled ? aiEditStatus.hotkeyLabel || "AI Edit" : null}
            />
          </div>
          {typeof onStop === "function" && (
            <button
              type="button"
              onClick={onStop}
              className={COMPACT_STOP_BUTTON_CLASS}
              title="Stop recording"
              aria-label="Stop recording"
            >
              Stop
            </button>
          )}
        </div>

        {/* Live words; the one-line hint only while nothing else is shown (keeps the bar small). */}
        {(transcriptText || !aiEditBusy) && (
        <div className="mt-1.5">
          {transcriptText ? (
            <p
              dir="auto"
              className="text-xs text-gray-700 dark:text-gray-200 italic break-words whitespace-pre-wrap max-h-8 leading-4 overflow-y-auto"
            >
              {transcriptText}
            </p>
          ) : (
            <p className="text-[11px] text-gray-500 animate-pulse dark:text-gray-400 truncate leading-4">{helpText}</p>
          )}
        </div>
        )}

        {aiEditBusy && (
          <div
            className="mt-1.5 rounded-md border border-yellow-200 bg-yellow-50 dark:bg-gray-700 dark:border-gray-600 px-2 py-1"
            data-testid="ai-edit-status"
          >
            <p className="text-[10px] font-semibold text-yellow-900 dark:text-yellow-200 flex items-center gap-1.5 leading-4">
              {aiEditStatus.phase === "sending" && (
                <span className="inline-flex w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
              )}
              {aiEditStatus.phase === "sending"
                ? t("editReportAi.sending", { defaultValue: "AI is editing the report…" })
                : t("editReportAi.collecting", { defaultValue: "AI edit — pause to send" })}
            </p>
            {aiEditStatus.instruction && (
              <p dir="auto" className="text-xs text-gray-800 dark:text-gray-100 break-words max-h-8 leading-4 overflow-y-auto">
                {aiEditStatus.instruction}
              </p>
            )}
          </div>
        )}
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      aria-label={`Voice input active in ${languageLabel}`}
      className={`${POPOVER_SHELL_BASE_CLASS} ${
        isRadicalAuth ? POPOVER_POSITION_CLASS.radical : POPOVER_POSITION_CLASS.default
      }`}
    >
      {!isRadicalAuth && (
        <div className="relative w-8 h-8 flex-shrink-0" aria-hidden="true">
          <div className="w-8 h-8 bg-green dark:bg-green-dark rounded-full flex items-center justify-center">
            <FaMicrophone className="w-4 h-4 text-gray-900 dark:text-gray-100" />
          </div>
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-gray-700 dark:text-gray-200 font-semibold leading-snug flex items-center gap-2">
              <span className="inline-flex w-2 h-2 rounded-full bg-green dark:bg-green-dark animate-pulse" />
              Listening...
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {languageLabel}
            </p>
          </div>

          <div className="flex items-start justify-end gap-2 flex-nowrap">
            {typeof onStop === "function" && (
              <button
                type="button"
                onClick={onStop}
                className={STOP_BUTTON_CLASS}
                title="Stop recording"
                aria-label="Stop recording"
              >
                Stop
              </button>
            )}

            <div className="relative group inline-flex">
              <span
                className={getModePillClass(isCommandMode, isAiEdit)}
                aria-label={isCommandMode ? "Command mode" : "Dictation mode"}
                aria-describedby={tooltipId}
                tabIndex={0}
              >
                {modeLabel}
              </span>
              {variant === "sidebar" ? (
                <SidebarHotkeysTooltip id={tooltipId} />
              ) : (
                <EditorHotkeysTooltip id={tooltipId} aiEdit={aiEditEnabled ? aiEditStatus.hotkeyLabel || "AI Edit" : null} />
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
          {transcriptText ? (
            <p className="text-sm text-gray-700 dark:text-gray-200 italic break-words whitespace-pre-wrap max-h-28 overflow-y-auto">
              {transcriptText}
            </p>
          ) : (
            <p className="text-sm text-gray-500 animate-pulse dark:text-gray-400">
              {helpText}
            </p>
          )}
        </div>

        {aiEditBusy && (
          <div
            className="mt-3 rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-gray-700 dark:border-gray-600 px-3 py-2"
            data-testid="ai-edit-status"
          >
            <p className="text-[11px] font-semibold text-yellow-900 dark:text-yellow-200 flex items-center gap-2">
              {aiEditStatus.phase === "sending" && (
                <span className="inline-flex w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
              )}
              {aiEditStatus.phase === "sending"
                ? t("editReportAi.sending", { defaultValue: "AI is editing the report…" })
                : t("editReportAi.collecting", { defaultValue: "AI edit — pause to send" })}
            </p>
            {aiEditStatus.instruction && (
              <p
                dir="auto"
                className="text-sm text-gray-800 dark:text-gray-100 mt-1 break-words max-h-20 overflow-y-auto"
              >
                {aiEditStatus.instruction}
              </p>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default RecordingMicPopover;
